import type {NavigationProp} from '@react-navigation/native';

import type {
  ApplePayWebConfig,
  PaymentMethodType,
} from '../../../components/payments/till/PaymentMethodSelector/types';
import {AppRoute} from '../../../models/AppRoute';
import type {AppRoutesNavigatorParamList} from '../../../models/navigationTypes';

export type CheckoutStep = 'cart' | 'checkout' | 'review' | 'payment';

/**
 * Navigation type for checkout screens.
 * Uses NavigationProp from React Navigation for proper type checking.
 */
export type CheckoutNavigation = NavigationProp<AppRoutesNavigatorParamList>;

export interface UnifiedCheckoutScreenProps {
  step: CheckoutStep;
  navigation: CheckoutNavigation;
}

export interface StepContentProps {
  navigation: CheckoutNavigation;
  /** Selected payment method (payment step only) */
  selectedPaymentMethod?: PaymentMethodType;
  /** Callback to change payment method (payment step only) */
  onPaymentMethodChange?: (method: PaymentMethodType) => void;
  /** Apple Pay configuration for web (payment step only) */
  applePayConfig?: ApplePayWebConfig;
  /** Callback when payment readiness changes (payment step only) */
  onCanPayChange?: (canPay: boolean) => void;
}

export interface CheckoutStepperProps {
  currentStep: CheckoutStep;
  onStepPress: (step: CheckoutStep) => void;
}

export interface CheckoutStepConfig {
  key: CheckoutStep;
  label: string;
  /** Title displayed in the header above the two-column layout */
  title: string;
  route: AppRoute;
  actionLabel: string;
}

export const CHECKOUT_STEPS: CheckoutStepConfig[] = [
  {
    key: 'cart',
    label: 'Cart',
    title: 'YOUR CART',
    route: AppRoute.CART,
    actionLabel: 'CONTINUE TO CHECKOUT',
  },
  {
    key: 'checkout',
    label: 'Delivery',
    title: 'DELIVERY OR PICKUP',
    route: AppRoute.CART_ORDER_CHECKOUT,
    actionLabel: 'REVIEW ORDER',
  },
  {
    key: 'review',
    label: 'Review',
    title: 'REVIEW YOUR ORDER',
    route: AppRoute.CART_ORDER_CHECKOUT_REVIEW,
    actionLabel: 'GO TO PAYMENT',
  },
  {
    key: 'payment',
    label: 'Payment',
    title: 'CHECKOUT PAYMENT',
    route: AppRoute.CART_ORDER_CHECKOUT_PAYMENT,
    actionLabel: 'PAY NOW',
  },
];

export function getStepIndex(step: CheckoutStep): number {
  return CHECKOUT_STEPS.findIndex(s => s.key === step);
}

export function getStepConfig(step: CheckoutStep): CheckoutStepConfig | undefined {
  return CHECKOUT_STEPS.find(s => s.key === step);
}
